import { useState } from 'react';
import { X, MapPin, ChartBar, CurrencyCircleDollar, MapTrifold, Crosshair, Buildings, CaretDown } from '@phosphor-icons/react';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <MapPin size={18} className="text-emerald-600" />,
    content: [
      { heading: 'Running a Valuation', text: 'Enter a property address in the sidebar or click directly on the map to place a pin. Select a metro area, set property features (bedrooms, bathrooms, security, pool, DSTV), then click "Run Valuation". The model analyses 1,900+ comparable properties and spatial factors to estimate rent.' },
      { heading: 'Selecting a Location', text: 'You can type any South African address, or click anywhere on the map. The pin will snap to the clicked location and auto-populate coordinates.' },
      { heading: 'Navigating the Map', text: 'After running a valuation the map will zoom into your selected location. You can zoom in further or zoom out using the scroll wheel or the +/\u2013 controls. Right-click anywhere on the map to access additional options, including viewing the municipal boundary for that area.' },
      { heading: 'Metro Selection', text: 'Choose from 21 metropolitan and district municipalities. This helps the model narrow comparable properties to your region.' },
    ],
  },
  {
    id: 'metrics',
    title: 'Understanding Your Results',
    icon: <ChartBar size={18} className="text-blue-600" />,
    content: [
      { heading: 'Estimated Monthly Rent', text: 'The predicted rent in Rands per month, generated by an ensemble of OLS regression and XGBoost models trained on real SA property data. The confidence band shows the likely range.' },
      { heading: 'Property Value', text: 'An estimated market value for the property based on rental yield analysis and comparable property data in the surrounding area.' },
      { heading: 'Location Score', text: 'A composite score (0\u201310) reflecting the desirability of the location based on proximity to amenities, transport, schools, healthcare, and commercial activity.' },
      { heading: "Nkosi\u2019s Opportunity Index (OI)", text: "A proprietary 0\u201310 score measuring the viability of informal retail (spaza/tuck shop) at that location. It combines 8 factors: population density, income levels, foot traffic estimates, competitor saturation, facility proximity, transport access, commercial activity, and market demand. Scores: 8\u201310 = Excellent, 6\u20138 = Good, 4\u20136 = Moderate, 2\u20134 = Below Average, 0\u20132 = Poor." },
    ],
  },
  {
    id: 'layers',
    title: 'Map Layers Explained',
    icon: <MapTrifold size={18} className="text-teal-600" />,
    content: [
      { heading: 'Walk-Time Zones', text: 'Concentric rings showing 5, 10, 15, and 20-minute walking distances from the selected location. Hover over each zone to see estimated population, customer capture percentage, and spaza cluster count. Useful for understanding the catchment area a pedestrian customer base can cover.' },
      { heading: 'Facilities', text: 'Multi-coloured pins for nearby public facilities: schools (blue), clinics/hospitals (red), community centres (green), and government offices (purple). Proximity to these drives foot traffic.' },
      { heading: 'Population', text: 'Green circles showing population density heatmap. Larger circles indicate denser populations\u2014more potential customers for retail.' },
      { heading: 'LSM Income Zones', text: 'Coloured ward boundaries based on the Living Standards Measure (LSM 1\u201310). Red = low income, Green = high income. This reflects the spending power of the local community.' },
      { heading: "Nkosi\u2019s Opportunity Index Honeycomb", text: 'Hexagonal grid cells coloured by Opportunity Index score. Green cells have high opportunity, red cells have low. Hover over any cell to see the exact score and interpretation.' },
      { heading: 'Market Fit', text: 'Rectangular grid patches showing a composite score (0\u2013100%) that evaluates how well each micro-area matches ideal retail conditions. Factors include population density, income, competitor proximity, facility access, and foot traffic. Green (\u226570%) = Strong, Yellow (40\u201370%) = Moderate, Orange/Red (<40%) = Weak.' },
      { heading: 'Comparables', text: 'Dark blue dots showing the comparable properties used by the model. Hover to see address, rent, and distance from your pin.' },
      { heading: 'Radius Colors', text: 'Concentric distance rings (5\u201335 km) with average rent displayed. Shows how rent changes as you move further from the selected point.' },
    ],
  },
  {
    id: 'spatial',
    title: 'Spatial Intelligence Tab',
    icon: <Crosshair size={18} className="text-purple-600" />,
    content: [
      { heading: 'Huff Gravity Model', text: 'Predicts market share capture probability based on your location\u2019s attractiveness relative to competitors. Shows estimated monthly revenue, market share percentage, and cannibalization risk (low/moderate/high).' },
      { heading: 'Trade Area Analysis', text: 'Walking-time isochrones at 5, 10, 15, and 20 minutes showing population and household counts within each zone. Critical for understanding your real customer base.' },
      { heading: 'Competitor Landscape', text: 'Breakdown of competing spaza shops within the trade area: number of clusters, estimated total shops, and density per square kilometer.' },
      { heading: 'LSM Demographics', text: 'Detailed Living Standards Measure data for the nearest ward: LSM band (1\u201310), average household income, population, unemployment rate. Helps gauge local spending power.' },
      { heading: 'Municipality Profile', text: 'Macro-level data for the local municipality: total population, households, growth rate, density, income, unemployment, youth percentage, and informal dwelling share.' },
    ],
  },
  {
    id: 'credits',
    title: 'Credits & Billing',
    icon: <CurrencyCircleDollar size={18} className="text-amber-600" />,
    content: [
      { heading: 'How Credits Work', text: 'Each valuation costs 1 credit (R250 excl. VAT). Credits are required to run valuations and access map layers.' },
      { heading: 'Buying Credits', text: 'Click your profile icon \u2192 "Buy Credits" to request additional credits from the administrator. You\u2019ll be notified once approved.' },
      { heading: 'Invoices', text: 'Go to Account Settings to download an auto-generated tax invoice (PDF) of your valuation history, or request a formal invoice from the administrator.' },
    ],
  },
  {
    id: 'reports',
    title: 'PDF Reports',
    icon: <Buildings size={18} className="text-slate-600" />,
    content: [
      { heading: 'Site Valuation Report', text: 'A comprehensive PDF covering: predicted rent, property value, Opportunity Index breakdown, spatial intelligence (Huff model, trade areas, competitor landscape), LSM demographics, municipality profile, and nearby facilities. Download from the sidebar after running a valuation.' },
      { heading: 'Property Valuation Report', text: 'A focused PDF with rent prediction, comparable properties table, and property-specific details. Designed for property owners and investors.' },
    ],
  },
];

function AccordionItem({ heading, text }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid={`guide-item-${heading.toLowerCase().replace(/\s+/g, '-')}`}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="text-xs font-semibold text-slate-700">{heading}</span>
        <CaretDown size={12} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-2.5 pt-0">
          <p className="text-xs text-slate-500 leading-relaxed">{text}</p>
        </div>
      )}
    </div>
  );
}

export default function UserGuidePage({ onClose }) {
  const [expandedSection, setExpandedSection] = useState('getting-started');

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="user-guide-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-base font-bold text-slate-800">User Guide</h2>
            <p className="text-xs text-slate-500">How to use the Automated Valuation Model</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="close-guide-btn">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {sections.map(s => {
            const isOpen = expandedSection === s.id;
            return (
              <div key={s.id} id={`guide-${s.id}`} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`guide-section-${s.id}`}>
                <button
                  onClick={() => setExpandedSection(isOpen ? null : s.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-left transition-colors ${isOpen ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                >
                  {s.icon}
                  <span className="text-sm font-bold text-slate-800 flex-1">{s.title}</span>
                  <CaretDown size={14} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 pt-1 space-y-1.5">
                    {s.content.map((item, idx) => (
                      <AccordionItem key={idx} heading={item.heading} text={item.text} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 text-center shrink-0">
          <p className="text-[10px] text-slate-400">
            {"Nkosi\u2019s Automated Valuation Model \u2014 Beaconwood Partners (Pty) Ltd"}
          </p>
        </div>
      </div>
    </div>
  );
}
